import { useState } from "react";
import {
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { joinTokenQuery, meQuery } from "@/lib/queries";
import { useJoinGame } from "@/lib/mutations";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/j/$token")({
  component: JoinPage,
  loader: async ({ context, params }) => {
    const info = await context.queryClient.ensureQueryData(
      joinTokenQuery(params.token),
    );
    return info;
  },
  beforeLoad: async ({ context, params }) => {
    // If the visitor already joined, skip the form and bounce to the lobby.
    try {
      const info = await context.queryClient.ensureQueryData(
        joinTokenQuery(params.token),
      );
      if (info.alreadyJoined) {
        throw redirect({
          to: "/games/$id/lobby",
          params: { id: info.game.id },
        });
      }
    } catch (e) {
      // ApiError (invalid token) → fall through, the component renders
      // an error card. redirect() throws too — re-throw it.
      if (e instanceof ApiError) return;
      throw e;
    }
  },
});

function JoinPage() {
  const router = useRouter();
  const { token } = Route.useParams();
  const infoQ = useQuery(joinTokenQuery(token));
  const meQ = useQuery(meQuery);
  const join = useJoinGame(token);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  // Pre-fill email if the visitor is already signed in.
  const signedInEmail = meQ.data?.user?.email ?? "";
  const emailToSubmit = email || signedInEmail;

  if (infoQ.error) {
    return (
      <section className="mx-auto mt-12 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Bad token</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-sm text-muted-foreground">
              That join link doesn't lead anywhere. Ask the host for a fresh one.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (!infoQ.data) return null;

  const { game } = infoQ.data;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || join.isPending) return;
    try {
      const res = await join.mutateAsync({
        name: trimmed,
        email: emailToSubmit ? emailToSubmit.trim() : undefined,
      });
      await router.navigate({
        to: "/games/$id/lobby",
        params: { id: res.gameId },
      });
    } catch {
      // surfaced via join.error
    }
  }

  const errorMessage =
    join.error instanceof ApiError
      ? join.error.message
      : join.error
      ? "Couldn't board the train. Try again."
      : null;

  return (
    <section className="mx-auto mt-10 max-w-md pb-32 md:pb-0">
      <Card>
        <CardHeader>
          <CardTitle>All aboard</CardTitle>
          <p className="font-mono text-xs text-muted-foreground">
            {game.totalRounds}-round game ·{" "}
            {game.rules.doubleBlankPenalty
              ? "double-blank = 50"
              : "double-blank = 0"}
          </p>
        </CardHeader>
        <CardContent>
          <form id="join-game" onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1">
              <Label htmlFor="name">Display name</Label>
              <Input
                id="name"
                type="text"
                autoComplete="name"
                required
                maxLength={32}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Buck"
              />
              <p className="font-mono text-[11px] text-muted-foreground">
                Up to 32 characters. The boys will see this on the scoreboard.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email">Email — your hat handle</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={signedInEmail || "optional"}
              />
              <p className="font-mono text-[11px] text-muted-foreground">
                Optional. Lets you keep stats across phones.
              </p>
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive"
              >
                {errorMessage}
              </div>
            )}
          </form>
        </CardContent>

        {/* Desktop CTA */}
        <div className="mt-5 hidden md:flex md:justify-end">
          <Button
            type="submit"
            form="join-game"
            size="lg"
            disabled={join.isPending || name.trim().length === 0}
          >
            {join.isPending ? "Boarding…" : "All aboard"}
          </Button>
        </div>
      </Card>

      {/* Mobile pinned-bottom CTA */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border/50 bg-sand-50/95 px-4 py-3 backdrop-blur md:hidden">
        <Button
          type="submit"
          form="join-game"
          size="lg"
          disabled={join.isPending || name.trim().length === 0}
          className="w-full"
        >
          {join.isPending ? "Boarding…" : "All aboard"}
        </Button>
      </div>
    </section>
  );
}
